import { cva, type VariantProps } from "../../lib";

// The row never grows past its container (S36: six md boxes overflowed a
// 375-wide dialog). Where the boxes don't fit, the gaps give way first, down to
// `spacing-xs`, and then the boxes, evenly and square (Ardhi's F7 answer,
// molecules audit, 2026-11-02). A box's size is its maximum, and
// `aspect-square` keeps it square as it shrinks.
export const otpRowClass = "flex max-w-full flex-row items-center";

// Each box sits in a cell, and the cell is what the row shrinks: the cell has
// no border, so a focused box's thicker one doesn't change its share
export const otpCellClass = "flex min-w-0 shrink flex-row";

// The gap between two boxes, or either side of the separator. A spacer rather
// than CSS `gap`, so it can shrink: flexbox and Yoga share a shortfall by
// shrink factor times size, so the spacers' large factor gives it to them until
// they reach their minimum, and only then to the cells. The cells' small share
// before that is under 0.05px.
export const otpSpacerVariants = cva("min-w-spacing-xs shrink-[1000]", {
  variants: {
    gap: {
      compact: "w-spacing-sm",
      default: "w-spacing-md",
      spacious: "w-spacing-lg",
    },
  },
  defaultVariants: {
    gap: "default",
  },
});

// On a touch screen the box doesn't clip, so its input can reach past it to a
// 44 tap area (S39); native boxes never clip, for the same reason
export const otpBoxVariants = cva(
  "web:overflow-hidden relative aspect-square min-w-0 shrink items-center justify-center border [@media(pointer:coarse)]:overflow-visible",
  {
    variants: {
      // The default box steps up from 768 (Ardhi's DQ26 answer); Small and
      // Large keep their size
      size: {
        sm: "rounded-radius-md w-otp-cell-sm",
        md: "rounded-radius-md w-otp-cell-md md:w-otp-cell-md-dt",
        lg: "rounded-radius-lg w-otp-cell-lg",
      },
      state: {
        default: "",
        error: "border-luce-system-danger-default bg-luce-system-danger-surface",
        success: "border-luce-system-success-default bg-luce-system-success-surface",
      },
      disabled: {
        true: "border-luce-neutral-border-mediumEmphasis bg-luce-neutral-background-intermediate",
        false: "",
      },
      focused: {
        true: "",
        false: "",
      },
      filled: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        state: "default",
        disabled: false,
        focused: true,
        class: "border-luce-brand-primaryBorder bg-luce-neutral-background-default border-1-5",
      },
      {
        state: "default",
        disabled: false,
        focused: false,
        filled: true,
        class: "border-luce-neutral-border-highEmphasis bg-luce-neutral-background-default",
      },
      {
        state: "default",
        disabled: false,
        focused: false,
        filled: false,
        class: "border-luce-neutral-border-mediumEmphasis bg-luce-neutral-background-default",
      },
      {
        state: "error",
        disabled: false,
        focused: true,
        class: "border-1-5",
      },
      {
        state: "success",
        disabled: false,
        focused: true,
        class: "border-1-5",
      },
    ],
    defaultVariants: {
      size: "md",
      state: "default",
      disabled: false,
      focused: false,
      filled: false,
    },
  },
);

// S39 (molecules audit 2026-10-30, OTPInputField #11): each digit's input is
// its cell's tap area, and DS-030 asks for 44. The input is transparent, so it
// can reach past its box without changing the look.
// - Web, on a touch screen: 44 tall, centred; sideways at most 3px from the
//   box's inside (2px past its 1px border), half the smallest gap, so
//   neighbours never share a strip however far the gaps have shrunk. A full
//   small cell (40) reaches 44; a shrunk one, its width plus 4; 44-and-over
//   cells don't change.
// - Native: the small cell's input sits 3pt outside the box's 1pt border on
//   each side, 2pt past its edge (44 at 40, within half the smallest gap). A
//   shrunk native cell keeps the same reach.
// The digit is Inter SemiBold on every platform, at the OTP-only sizes
// (OTPInputField #9; Ardhi's DQ26 answer).
// (Tailwind finds classes only when they're written out whole.)
export const otpInputVariants = cva(
  [
    "text-luce-neutral-foreground-default web:ring-0 web:focus:ring-0 web:focus-visible:outline-none web:focus-visible:ring-0 p-spacing-0 native:font-inter-semibold border-none bg-transparent text-center font-semibold leading-none outline-none selection:bg-transparent focus:outline-none",
    "[@media(pointer:coarse)]:absolute [@media(pointer:coarse)]:top-[min(0px,calc(50%-22px))] [@media(pointer:coarse)]:h-[max(100%,44px)]",
    "[@media(pointer:coarse)]:left-[max(-3px,min(0px,calc(50%-22px)))] [@media(pointer:coarse)]:w-[min(calc(100%+6px),max(100%,44px))]",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "web:h-full web:w-full native:absolute native:top-[-3px] native:right-[-3px] native:bottom-[-3px] native:left-[-3px] text-size-otp-digit-sm",
        md: "text-size-otp-digit-md h-full w-full",
        lg: "text-size-otp-digit-lg h-full w-full",
      },
      disabled: {
        true: "text-luce-neutral-foreground-attenuated",
        false: "",
      },
      tone: {
        default: "",
        placeholder: "placeholder:text-luce-neutral-foreground-attenuated",
      },
    },
    defaultVariants: {
      size: "md",
      disabled: false,
      tone: "default",
    },
  },
);

export type OTPSpacerVariantsProps = VariantProps<typeof otpSpacerVariants>;
export type OTPBoxVariantsProps = VariantProps<typeof otpBoxVariants>;
export type OTPInputVariantsProps = VariantProps<typeof otpInputVariants>;
